import { ArrowRight, CheckCircle2, Layers, PlugZap, ShieldCheck, Sparkles } from "lucide-react";
import { NavLink } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const overviewHighlights = [
  {
    title: "Rule-guided creative pipelines",
    description:
      "Codify creative decisions as reusable logic blocks. STRONOM Design keeps every iteration aligned with brand, material, and manufacturing rules.",
    icon: Sparkles,
  },
  {
    title: "Verification-first delivery",
    description:
      "Proof layers evaluate outputs for compliance, manufacturability, and sustainability before anything leaves the studio.",
    icon: ShieldCheck,
  },
  {
    title: "Cross-discipline collaboration",
    description:
      "Bring design, sourcing, and engineering stakeholders into a shared governance model with auditable decisions.",
    icon: Layers,
  },
];

const studioCards = [
  {
    title: "Footwear Studio",
    status: "Active",
    description:
      "Rule-guided workspace for footwear creation — from intent capture to production validation. Current focus: athletic, lifestyle, and performance classes.",
    cta: {
      label: "Launch Footwear Studio",
      href: "/design/footwear",
    },
  },
  {
    title: "Apparel Studio",
    status: "In Development",
    description: "Structured garment workflows for pattern-driven design and compliance-ready bill of materials.",
  },
  {
    title: "Accessories Studio",
    status: "In Development",
    description: "From bags to wearable tech, ensure every component follows provenance and durability standards.",
  },
  {
    title: "Furniture Studio",
    status: "In Development",
    description: "Design interior systems with embedded sustainability metrics, safety checks, and supplier validation.",
  },
];

const ruleLibraries = [
  {
    title: "Design Intent",
    bullets: [
      "Style directives, silhouettes, and macro aesthetics",
      "Colorway controls with seasonal palettes",
      "Material inheritance with regional compliance data",
    ],
  },
  {
    title: "Manufacturing",
    bullets: [
      "Tolerance rules per component and assembly",
      "Factory capability matching and approvals",
      "Traceable change requests with audit trails",
    ],
  },
  {
    title: "Compliance",
    bullets: [
      "Regulatory guardrails mapped to geography",
      "Ethical sourcing and sustainability benchmarks",
      "Immutable handoff packages for legal and QA",
    ],
  },
];

const connectors = [
  {
    title: "Creative and PLM",
    items: [
      "Adobe Creative Cloud",
      "CLO3D",
      "Bespoke metadata APIs",
    ],
  },
  {
    title: "Supply and Manufacturing",
    items: [
      "NetSuite & ERP bridges",
      "Supplier credential APIs",
      "Smart factory adapters",
    ],
  },
  {
    title: "Governance",
    items: [
      "Internal policy engines",
      "Compliance monitoring",
      "Audit & reporting suites",
    ],
  },
];

const roadmap = [
  {
    milestone: "Q2 2024",
    title: "Partner onboarding",
    description: "Joint design councils and tier-one footwear brands gain early studio access.",
  },
  {
    milestone: "Q3 2024",
    title: "Rule marketplace",
    description: "Launch curated rule packs for apparel, materials, and sustainability programs.",
  },
  {
    milestone: "Q4 2024",
    title: "Connector expansion",
    description: "Native integrations with ERP, compliance, and digital twin ecosystems.",
  },
];

const tabClassName = "data-[state=active]:bg-stronom-design/15 data-[state=active]:text-stronom-design";

const Design = () => {
  return (
    <div className="relative">
      <section className="relative overflow-hidden pb-24 pt-16">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-stronom-design/20 blur-[160px]" />
        <div className="pointer-events-none absolute bottom-0 right-[-15%] h-[320px] w-[320px] rounded-full bg-stronom-gov/15 blur-[140px]" />
        <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center">
          <div className="space-y-8">
            <Badge variant="outline" className="border-stronom-design/40 bg-stronom-design/10 text-stronom-design">
              Active Module
            </Badge>
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.45em] text-stronom-agents/70">STRONOM DESIGN</p>
              <h1 className="font-display text-4xl font-semibold leading-[1.05] md:text-5xl">
                Turning creative intent into structured, manufacturable design.
              </h1>
              <p className="text-lg text-muted-foreground">
                STRONOM Design brings structure to creative workflows. It validates every decision, protects brand DNA,
                and produces evidence for manufacturing and compliance teams.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-[0_18px_60px_hsl(var(--color-design)_/_0.35)]">
                <NavLink to="/design/footwear" className="inline-flex items-center gap-2">
                  Launch Footwear Studio
                  <ArrowRight className="size-4" />
                </NavLink>
              </Button>
              <Button variant="ghost" className="text-sm text-muted-foreground hover:text-foreground" asChild>
                <a href="mailto:contact@stronom.com?subject=STRONOM%20Design%20Partnership">Partner with STRONOM</a>
              </Button>
            </div>
            <div className="grid gap-4 rounded-3xl border border-border/60 bg-card/80 p-6 backdrop-blur md:grid-cols-3">
              {overviewHighlights.map((item) => (
                <div key={item.title} className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/75">
                    <item.icon className="size-4" />
                    {item.title}
                  </div>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -translate-y-6 rounded-[3rem] bg-gradient-to-br from-white/10 via-white/4 to-transparent blur-3xl" />
            <div className="relative overflow-hidden rounded-[2.75rem] border border-white/10 bg-black/40 p-10 shadow-[0_45px_160px_rgba(5,10,22,0.6)] backdrop-blur">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.35em] text-stronom-design/70">
                <span className="flex items-center gap-3">
                  <CheckCircle2 className="size-4" />
                  Proof State
                </span>
                <span className="rounded-full bg-stronom-design/15 px-3 py-1 text-[10px] text-stronom-design">Live</span>
              </div>
              <div className="mt-8 space-y-4 text-sm text-muted-foreground">
                <p>• Immutable logs for every concept iteration</p>
                <p>• Automated compliance checks across material, region, and production rules</p>
                <p>• Instant audit packages for manufacturing partners</p>
              </div>
              <div className="mt-10 rounded-2xl border border-white/10 bg-background/70 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                  Studio Status
                </p>
                <p className="mt-2 text-sm text-muted-foreground">Footwear Studio in production • Additional studios onboarding</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative pb-28">
        <div className="mx-auto max-w-6xl px-6">
          <Tabs defaultValue="overview" className="space-y-12">
            <TabsList className="flex w-full flex-wrap justify-start gap-2 rounded-2xl border border-border/70 bg-card/80 p-2">
              <TabsTrigger value="overview" className={tabClassName}>
                Overview
              </TabsTrigger>
              <TabsTrigger value="studios" className={tabClassName}>
                Studios
              </TabsTrigger>
              <TabsTrigger value="rules" className={tabClassName}>
                Rule Libraries
              </TabsTrigger>
              <TabsTrigger value="connectors" className={tabClassName}>
                Connectors
              </TabsTrigger>
              <TabsTrigger value="roadmap" className={tabClassName}>
                Roadmap
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <div className="grid gap-6 rounded-3xl border border-border/60 bg-card/80 p-10 shadow-[0_30px_120px_rgba(6,12,24,0.55)] backdrop-blur md:grid-cols-2">
                <div className="space-y-4">
                  <h2 className="font-display text-3xl font-semibold text-foreground">Framework Overview</h2>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    STRONOM Design orchestrates creative pipelines with proof-based checkpoints. Designers work in their
                    native tools while STRONOM validates every action, keeps metadata synchronized, and prepares
                    submission-ready packages for manufacturing, sourcing, and legal teams.
                  </p>
                </div>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <p>• Structured workflows with configurable stage gates</p>
                  <p>• Context-aware prompts, materials, and sustainability lenses</p>
                  <p>• Role-based workspaces aligned to brand governance</p>
                  <p>• Ready-to-deploy connectors for PLM, ERP, and compliance suites</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="studios">
              <div className="grid gap-6 rounded-3xl border border-border/60 bg-card/80 p-10 shadow-[0_30px_120px_rgba(6,12,24,0.55)] backdrop-blur sm:grid-cols-2">
                {studioCards.map((studio) => (
                  <div key={studio.title} className="flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-background/70 p-6">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.35em] text-stronom-design/70">{studio.status}</p>
                        <h3 className="mt-2 text-xl font-semibold text-foreground">{studio.title}</h3>
                      </div>
                      {studio.status === "Active" ? (
                        <Badge className="bg-stronom-design/20 text-stronom-design">Live</Badge>
                      ) : (
                        <Badge variant="outline" className="border-border/60 text-muted-foreground">
                          Coming Soon
                        </Badge>
                      )}
                    </div>
                    <p className="flex-1 text-sm text-muted-foreground">{studio.description}</p>
                    {studio.cta ? (
                      <Button asChild className="mt-auto shadow-[0_18px_60px_hsl(var(--color-design)_/_0.35)]">
                        <NavLink to={studio.cta.href} className="inline-flex items-center gap-2">
                          {studio.cta.label}
                          <ArrowRight className="size-4" />
                        </NavLink>
                      </Button>
                    ) : (
                      <Button variant="outline" className="mt-auto border-dashed border-border/60 text-muted-foreground" disabled>
                        In Development
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="rules" className="space-y-6">
              <div className="rounded-3xl border border-border/60 bg-card/80 p-10 shadow-[0_30px_120px_rgba(6,12,24,0.55)] backdrop-blur">
                <h2 className="text-2xl font-semibold text-foreground">Rule Libraries</h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  Libraries are modular collections of declarative rules. They are versioned, reviewable, and auditable
                  before going live in the studio.
                </p>
                <div className="mt-8 grid gap-6 md:grid-cols-3">
                  {ruleLibraries.map((library) => (
                    <div key={library.title} className="rounded-2xl border border-border/60 bg-background/70 p-6">
                      <h3 className="text-lg font-semibold text-foreground">{library.title}</h3>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {library.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-stronom-design" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="connectors" className="space-y-6">
              <div className="rounded-3xl border border-border/60 bg-card/80 p-10 shadow-[0_30px_120px_rgba(6,12,24,0.55)] backdrop-blur">
                <h2 className="text-2xl font-semibold text-foreground">Connectors</h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  STRONOM Design integrates with the tools and systems that already drive your brand operations.
                </p>
                <div className="mt-8 grid gap-6 md:grid-cols-3">
                  {connectors.map((group) => (
                    <div key={group.title} className="rounded-2xl border border-border/60 bg-background/70 p-6">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                        <PlugZap className="size-4" />
                        {group.title}
                      </div>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {group.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="roadmap" className="space-y-6">
              <div className="rounded-3xl border border-border/60 bg-card/80 p-10 shadow-[0_30px_120px_rgba(6,12,24,0.55)] backdrop-blur">
                <h2 className="text-2xl font-semibold text-foreground">Roadmap</h2>
                <div className="mt-6 space-y-6">
                  {roadmap.map((item) => (
                    <div key={item.milestone} className="rounded-2xl border border-border/60 bg-background/70 p-6">
                      <p className="text-xs uppercase tracking-[0.35em] text-stronom-design/70">{item.milestone}</p>
                      <h3 className="mt-2 text-lg font-semibold text-foreground">{item.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Design;
